import { useEffect, useRef } from 'react';
import type { BadgeView } from '@contracts';
import { BadgeGraphic } from '@ui/shared/badges/BadgeGraphic';
import { describeCriterion } from '@ui/shared/badges/criterionText';

/**
 * Badge earn moment (badges-and-celebrations.md SS1.4) — centered panel,
 * queued strictly AFTER any level-up (never stacked audio-visual chaos, per
 * spec). Badge renders in its earned (luminous) state immediately — no
 * scale-in/glow-bloom keyframe system implemented here beyond the CSS
 * transition on mount, matching this codebase's existing pattern of leaning
 * on tokens.css's reduced-motion collapse rather than a bespoke animation
 * library.
 */
export function BadgeEarnSheet({
  badge,
  onDismiss,
}: {
  readonly badge: BadgeView;
  readonly onDismiss: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    panelRef.current?.focus();
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onDismiss();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onDismiss]);

  const earnedOn = badge.state.earned ? badge.state.earnedOn : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Badge earned: ${badge.name}`}
      onClick={onDismiss}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'color-mix(in srgb, var(--surface-base) 70%, transparent)',
        padding: 'var(--space-6)',
      }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'var(--space-4)',
          background: 'var(--surface-overlay)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-3)',
          padding: 'var(--space-8)',
          maxWidth: '340px',
          textAlign: 'center',
        }}
      >
        <BadgeGraphic iconId={badge.iconId} earned category={null} size={112} progressFraction={1} />
        <h2 style={{ margin: 0, fontSize: 'var(--type-heading-size)', fontWeight: 'var(--type-heading-weight)', color: 'var(--text-primary)' }}>
          {badge.name}
        </h2>
        <p style={{ margin: 0, fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
          {describeCriterion(badge.criterion)}
          {earnedOn ? ` — Earned ${earnedOn}.` : ''}
        </p>
        <button
          type="button"
          onClick={onDismiss}
          style={{
            marginTop: 'var(--space-2)',
            minHeight: 'var(--touch-target-min)',
            padding: '0 var(--space-6)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--hue-primary-vivid)',
            color: 'var(--text-on-accent)',
            fontWeight: 600,
            fontSize: 'var(--type-body-size)',
            border: '1px solid var(--hue-primary-strong)',
          }}
        >
          Nice — keep going
        </button>
      </div>
    </div>
  );
}
